// Native Studio Inc 1 — /studio/$slug: the shared publisher studio. Resolves the
// room and renders StudioStage (which does PreJoin → mint-with-name → role-gate:
// host/cohost publish, viewer/gated → watch page). Anon → sign-in.

import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { roomQueries } from '@/features/live/roomQueries';
import { StudioStage } from '@/features/live/StudioStage';
import { useAuth } from '@/lib/auth/useAuth';

export const Route = createFileRoute('/studio/$slug')({
  component: StudioRoute,
});

function Status({ label }: { label: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-video-bg">
      <p className="font-sans text-sm text-video-text-faint">{label}</p>
    </div>
  );
}

function Centered({ title, action }: { title: string; action?: { href: string; label: string } }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-video-bg px-6 text-center">
      <h1 className="font-serif text-2xl text-video-text">{title}</h1>
      {action && (
        <a
          href={action.href}
          className="rounded-md bg-video-text px-5 py-2 font-sans text-sm font-medium text-video-bg"
        >
          {action.label}
        </a>
      )}
      <a href="/" className="font-sans text-xs text-video-text-faint hover:underline">
        Back to browse
      </a>
    </div>
  );
}

function StudioRoute() {
  const { slug } = Route.useParams();
  const { session } = useAuth();
  const roomQuery = useQuery(roomQueries.bySlug(slug));
  const room = roomQuery.data;

  if (roomQuery.isLoading) return <Status label="Loading…" />;
  if (!room) return <Centered title="This stream isn't available" />;
  if (!session) {
    // Standing rule (4th instance): any auth-adjacent CTA carries `next`.
    return (
      <Centered
        title="Sign in to go on air"
        action={{
          href: `/auth/sign-in?next=${encodeURIComponent(`/studio/${slug}`)}`,
          label: 'Sign in',
        }}
      />
    );
  }
  return <StudioStage room={room} />;
}
